import { useCallback, useEffect, useRef, useState } from "react"
import { loadStripe } from "@stripe/stripe-js"
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js"
import { Button } from "@/components/ui/button"
import { CheckBadgeIcon } from "@heroicons/react/24/outline"
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/16/solid"
import { useGetAuthData } from "@/lib/hooks"
import { client, validateClientProperty } from "@/lib/ts-rest"
import { trackEvent } from "@/lib/log"

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null

function SubscribeCheckout({
  onCheckoutComplete,
}: {
  onCheckoutComplete: () => void
}) {
  const { mutateAsync } = client.createCheckoutSession.useMutation()

  const fetchClientSecret = useCallback(async () => {
    trackEvent("started checkout")
    const result = await mutateAsync({})
    return result.body.clientSecret
  }, [mutateAsync])

  const onComplete = useCallback(() => {
    trackEvent("completed checkout")
    onCheckoutComplete()
  }, [onCheckoutComplete])

  return (
    <div className="max-w-md rounded-3xl overflow-hidden py-4 bg-primary">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{ fetchClientSecret, onComplete }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  )
}

function ManageBilling({ hasStripeCustomer }: { hasStripeCustomer: boolean }) {
  const [loading, setLoading] = useState(false)
  const { refetch } = client.getStripePortalLink.useQuery({
    queryKey: [validateClientProperty("getStripePortalLink")],
    enabled: false,
  })

  async function handleClick() {
    trackEvent("opened billing management")
    setLoading(true)
    try {
      const { data } = await refetch()
      if (data?.body.url) {
        window.open(data.body.url, "_blank")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-4 w-fit">
        <CheckBadgeIcon className="h-6 w-6 shrink-0" />
        <span className="font-medium">Subscription active</span>
      </div>
      {hasStripeCustomer && (
        <Button
          variant="outline"
          className="w-fit"
          onClick={handleClick}
          disabled={loading}
        >
          {loading ? "Loading..." : "Manage billing"}
          {!loading && <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />}
        </Button>
      )}
    </div>
  )
}

export function Payment() {
  const [pollEnabled, setPollEnabled] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data } = client.getAuthData.useQuery({
    queryKey: [validateClientProperty("getAuthData")],
    refetchInterval: (query) =>
      pollEnabled && !query.state.data?.body.user.hasSpeakerSubscription
        ? 1000
        : false,
  })
  const user = data?.body.user

  useEffect(() => {
    if (pollEnabled && user?.hasSpeakerSubscription) {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }
  }, [pollEnabled, user?.hasSpeakerSubscription])

  if (!user) return null

  return (
    <div ref={containerRef}>
      {user.hasSpeakerSubscription ? (
        <ManageBilling hasStripeCustomer={!!user.stripeCustomerId} />
      ) : (
        <SubscribeCheckout onCheckoutComplete={() => setPollEnabled(true)} />
      )}
    </div>
  )
}
